import { useEffect, useState } from 'react';
import Modal from '../modal/Modal';
import NumberInput from '../custom-input/NumberInput';
import { Account } from '../../types/interfaces/account.interfaces';
import { AccountOnFarmBillingOverride } from '../../types/interfaces/onFarmBillingOverride.interfaces';
import { centsToDollars, dollarsToCents } from '../../services/account.service';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS, ON_FARM_BILLING_CONSTANTS } from '../../constants/common';
import './onFarmCustomQuoteModal.scss';

export type OnFarmCustomQuoteForm = {
  customMonthlyFeeCents: number;
  customMaxTanks: number;
  notes?: string;
};

type OnFarmCustomQuoteModalProps = {
  isOpen: boolean;
  width?: number;
  account?: Account;
  override?: AccountOnFarmBillingOverride;
  onClose: () => void;
  onConfirm: (data: OnFarmCustomQuoteForm) => void;
};

const OnFarmCustomQuoteModal = ({ isOpen, width = 750, account, override, onClose, onConfirm }: OnFarmCustomQuoteModalProps) => {
  const [monthlyFee, setMonthlyFee] = useState<string>('');
  const [maxTanks, setMaxTanks] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  const isEdit = Boolean(override?.accountOnFarmBillingOverrideId);

  useEffect(() => {
    if (!isOpen) return;
    setMonthlyFee(override?.customMonthlyFeeCents != null ? String(centsToDollars(override.customMonthlyFeeCents)) : '');
    setMaxTanks(override?.customMaxTanks != null ? String(override.customMaxTanks) : '');
    setNotes(override?.notes || '');
  }, [isOpen, override]);

  const feeValue = Number(monthlyFee);
  const tanksValue = Number(maxTanks);
  const isFeeValid = monthlyFee.trim().length > 0 && !Number.isNaN(feeValue) && feeValue >= 0;
  const isTanksValid = maxTanks.trim().length > 0 && Number.isInteger(tanksValue) && tanksValue >= 1;
  const canSave = isFeeValid && isTanksValid;

  const handleClose = () => {
    onClose();
  };

  const handleConfirm = () => {
    if (!canSave) return;
    onConfirm({ customMonthlyFeeCents: dollarsToCents(feeValue), customMaxTanks: tanksValue, notes });
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} width={width} ignoreBackdrop>
      <div className="on-farm-custom-quote-modal">
        <div className="header">
          <h4>{isEdit ? ON_FARM_BILLING_CONSTANTS.EDIT_CUSTOM_QUOTE : ON_FARM_BILLING_CONSTANTS.ADD_CUSTOM_QUOTE}</h4>
        </div>
        <div className="body">
          <div className="form-row">
            <label>{LABEL_CONSTANTS.ACCOUNT_OWNER}:</label>
            <div className="account-info">
              {account?.accountId} - {account?.name}
            </div>
          </div>
          <div className="form-row">
            <label>{ON_FARM_BILLING_CONSTANTS.MONTHLY_FEE} ($):</label>
            <NumberInput
              value={monthlyFee}
              min={0}
              step="0.01"
              placeholder="0.00"
              onChange={event => setMonthlyFee(event.target.value)}
            />
          </div>
          <div className="form-row">
            <label>{ON_FARM_BILLING_CONSTANTS.MAX_TANKS}:</label>
            <NumberInput value={maxTanks} min={1} step="1" placeholder="4" onChange={event => setMaxTanks(event.target.value)} />
          </div>
          <div className="form-row expanded-text-area">
            <label htmlFor="on-farm-quote-notes">{LABEL_CONSTANTS.NOTES}:</label>
            <textarea
              name="notes"
              id="on-farm-quote-notes"
              placeholder="Notes"
              onChange={event => setNotes(event.target.value)}
              rows={4}
              cols={50}
              value={notes}
            />
          </div>
        </div>
        <div className="footer">
          <button className="button blue inverted small" onClick={handleClose}>
            {BUTTON_CONSTANTS.CANCEL}
          </button>
          <button className="small green button" disabled={!canSave} onClick={handleConfirm}>
            {BUTTON_CONSTANTS.SAVE}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default OnFarmCustomQuoteModal;
